/**
 * SDK Embed Page
 * ==============
 * /sdk/embed: the verification flow for hosts that embed it in an iframe
 * (trustgate-embed.js) or a React Native WebView (react-native-trustgate).
 *
 * Handshake:
 * 1. This page posts {source:"trustgate", type:"ready"} to the host.
 * 2. The host replies {source:"trustgate-host", type:"init", token, theme?}.
 * 3. This page calls POST /api/v1/sdk/embed/session with the token, which
 *    checks the host origin for the tenant and returns branding + flags.
 * 4. The normal step engine renders with that token, held in memory only.
 *
 * Nothing here changes /sdk/verify.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2, RefreshCw, X } from 'lucide-react';
import VerificationSDK from './VerificationSDK';
import { SDKEmbedContext } from './embed/EmbedContext';
import {
  createEmbedBridge,
  createEmbedSession,
  getSdkApiBase,
  parseInitMessage,
} from './embed/embedBridge';

const INIT_TIMEOUT_MS = 20000;

const isNetworkMessage = (message) =>
  typeof message === 'string' && /failed to fetch|network|load failed/i.test(message);

export default function SDKEmbedPage() {
  const bridge = useMemo(() => createEmbedBridge(), []);
  const sdkBase = useMemo(() => getSdkApiBase(), []);

  // Token lives in React state only: never in the URL or browser storage.
  const [token, setToken] = useState(null);
  const [theme, setTheme] = useState('light');
  const [phase, setPhase] = useState('waiting'); // waiting | starting | ready | error | timeout
  const [session, setSession] = useState(null);
  const [failure, setFailure] = useState(null); // {code, message, retryable}

  const initReceivedRef = useRef(false);
  const tokenRef = useRef(null); // memory only
  const originRef = useRef(null);
  const finishedRef = useRef(false); // complete or cancel sent
  const abandonSentRef = useRef(false);
  const lastStepRef = useRef(null);
  const applicantIdRef = useRef(null);

  const emitError = useCallback((code, message, retryable) => {
    bridge.send('error', { code, message, retryable: !!retryable });
  }, [bridge]);

  // Exchange the token for an embed session (origin check + branding).
  const startSession = useCallback(async () => {
    const sdkToken = tokenRef.current;
    if (!sdkToken) return;
    setPhase('starting');
    setFailure(null);
    try {
      const data = await createEmbedSession(sdkBase, sdkToken, {
        parentOrigin: originRef.current,
        native: bridge.native,
      });
      applicantIdRef.current = data?.applicant_id || null;
      setSession(data || {});
      setPhase('ready');
    } catch (err) {
      const f = {
        code: err.code || 'session_error',
        message: err.message || 'We could not start the verification.',
        retryable: !!err.retryable,
      };
      setFailure(f);
      setPhase('error');
      emitError(f.code, f.message, f.retryable);
    }
  }, [sdkBase, bridge, emitError]);

  // Listen for init, then announce readiness.
  useEffect(() => {
    const onMessage = (event) => {
      if (initReceivedRef.current) return;
      const init = parseInitMessage(event, { native: bridge.native });
      if (!init) return;
      initReceivedRef.current = true;
      if (init.parentOrigin) bridge.lockOrigin(init.parentOrigin);
      tokenRef.current = init.token;
      originRef.current = init.parentOrigin;
      if (init.theme) setTheme(init.theme);
      setToken(init.token);
      startSession();
    };
    window.addEventListener('message', onMessage);
    // Android RN WebView delivers host messages on document.
    document.addEventListener('message', onMessage);
    bridge.send('ready');

    const timer = setTimeout(() => {
      if (!initReceivedRef.current) setPhase('timeout');
    }, INIT_TIMEOUT_MS);

    return () => {
      window.removeEventListener('message', onMessage);
      document.removeEventListener('message', onMessage);
      clearTimeout(timer);
    };
  }, [bridge, startSession]);

  // Events from the step engine.
  const embedApi = useMemo(() => ({
    onEvent(type, payload) {
      if (type === 'step') lastStepRef.current = payload?.step || null;
      if (type === 'complete') finishedRef.current = true;
      bridge.send(type, payload);
    },
    reportError({ code, message, retryable }) {
      const net = isNetworkMessage(message);
      bridge.send('error', {
        code: net ? 'network_error' : code,
        message,
        retryable: retryable !== false,
      });
    },
  }), [bridge]);

  const handleSdkError = useCallback((err) => {
    const message = err?.message || 'Something went wrong';
    bridge.send('error', {
      code: isNetworkMessage(message) ? 'network_error' : 'load_failed',
      message,
      retryable: true,
    });
  }, [bridge]);

  // Completion is reported through embedApi.onEvent('complete').
  const handleSdkComplete = useCallback(() => {}, []);

  const handleCancel = useCallback(() => {
    finishedRef.current = true;
    bridge.send('cancel', {
      applicantId: applicantIdRef.current,
      step: lastStepRef.current,
    });
  }, [bridge]);

  // Abandon: best effort, once, only after the session started and before
  // completion or cancel.
  useEffect(() => {
    const sendAbandon = () => {
      if (abandonSentRef.current || finishedRef.current || !applicantIdRef.current) return;
      abandonSentRef.current = true;
      bridge.send('abandon', {
        applicantId: applicantIdRef.current,
        lastStep: lastStepRef.current,
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') sendAbandon();
    };
    window.addEventListener('pagehide', sendAbandon);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', sendAbandon);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [bridge]);

  const branding = session?.branding && typeof session.branding === 'object' ? session.branding : null;
  const whiteLabel = session?.white_label === true;
  const prefill = session?.prefill && typeof session.prefill === 'object' ? session.prefill : null;
  const isDark = theme === 'dark';
  const pageBg = (typeof branding?.background_color === 'string' && branding.background_color)
    || (isDark ? '#0b0a10' : '#ffffff');

  return (
    <div
      className={`sdk-embed-page ${isDark ? 'dark' : 'light'}`}
      style={{ '--embed-bg': pageBg }}
    >
      {(phase === 'ready' || phase === 'error') && (
        <button
          type="button"
          className="sdk-embed-close"
          aria-label="Close verification"
          onClick={handleCancel}
        >
          <X size={20} />
        </button>
      )}

      {(phase === 'waiting' || phase === 'starting') && (
        <div className="sdk-embed-status" role="status">
          <Loader2 size={28} className="sdk-embed-spin" />
          <span>Preparing verification...</span>
        </div>
      )}

      {phase === 'timeout' && (
        <div className="sdk-embed-status">
          <AlertCircle size={28} />
          <span>This page must be opened from the app that started your verification.</span>
        </div>
      )}

      {phase === 'error' && failure && (
        <div className="sdk-embed-status" role="alert">
          <AlertCircle size={28} />
          <span>{failure.message}</span>
          {failure.retryable && (
            <button type="button" className="sdk-embed-retry" onClick={startSession}>
              <RefreshCw size={16} />
              <span>Try again</span>
            </button>
          )}
        </div>
      )}

      {phase === 'ready' && token && (
        <SDKEmbedContext.Provider value={embedApi}>
          <VerificationSDK
            accessToken={token}
            baseUrl={sdkBase}
            theme={isDark ? 'dark' : 'light'}
            onComplete={handleSdkComplete}
            onError={handleSdkError}
            embed={embedApi}
            whiteLabel={whiteLabel}
            prefill={prefill}
            brandingOverride={branding}
          />
        </SDKEmbedContext.Provider>
      )}

      <style>{EMBED_STYLES}</style>
    </div>
  );
}

const EMBED_STYLES = `
  html, body { margin: 0; background: var(--embed-bg, #ffffff); }
  .sdk-embed-page {
    position: relative;
    min-height: 100vh;
    min-height: 100dvh;
    background: var(--embed-bg);
    color: #111827;
    -webkit-text-size-adjust: 100%;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  }
  .sdk-embed-page.dark { color: #f3f4f6; }

  .sdk-embed-close {
    position: absolute;
    top: calc(10px + env(safe-area-inset-top));
    right: 10px;
    z-index: 20;
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 999px;
    background: rgba(127, 127, 127, 0.16);
    color: inherit;
    cursor: pointer;
  }
  .sdk-embed-close:focus-visible { outline: 2px solid var(--sdk-primary, #8b5cf6); outline-offset: 2px; }

  .sdk-embed-status {
    min-height: 100vh;
    min-height: 100dvh;
    box-sizing: border-box;
    padding: 24px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    text-align: center;
    font-size: 15px;
    line-height: 1.5;
    max-width: 420px;
    margin: 0 auto;
  }
  .sdk-embed-spin { animation: sdk-embed-spin 1s linear infinite; }
  @keyframes sdk-embed-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  .sdk-embed-retry, .sdk-embed-page .sdk-btn.secondary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 44px;
    padding: 12px 20px;
    border-radius: 12px;
    border: 1px solid rgba(127, 127, 127, 0.35);
    background: transparent;
    color: inherit;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
  .sdk-embed-page .embed-retry { margin-bottom: 12px; }

  /* Full-bleed card inside a host frame */
  .sdk-embed-page .verification-sdk {
    max-width: 480px;
    min-height: 100vh;
    min-height: 100dvh;
    border-radius: 0;
    box-shadow: none;
    background: var(--embed-bg);
  }
  .sdk-embed-page .sdk-progress-bar { padding-right: 56px; }

  /* A global .step-header rule (workbench.css) turns step headers into a
     row. Keep the SDK header stacked inside the embed. */
  .sdk-embed-page .verification-sdk .step-header { display: block; }
  /* Liveness intro: back button and a fixed 48px icon tile in one row. */
  .sdk-embed-page .verification-sdk .liveness-step .step-header {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .sdk-embed-page .verification-sdk .liveness-step .step-icon {
    width: 48px;
    height: 48px;
    flex: 0 0 48px;
  }
  /* The retake pill has a white background and no text color of its own, so
     it inherited the dark theme's light text. */
  .sdk-embed-page .verification-sdk .retake-btn { color: #111827; }

  /* Dark theme surfaces (the base SDK styles assume a light card) */
  .sdk-embed-page.dark .verification-sdk.dark {
    background: var(--embed-bg);
    --gray-50: #16141d;
    --gray-100: #1f1c29;
    --gray-200: #2e2a3b;
    --gray-300: #4a4458;
    --gray-400: #8b8698;
    --gray-500: #a8a3b5;
    --gray-600: #c9c5d4;
  }
  .sdk-embed-page.dark .verification-sdk .step-header .step-subtitle { color: var(--gray-500); }
  .sdk-embed-page.dark .verification-sdk .sdk-progress-bar { background: var(--embed-bg); border-color: var(--gray-200); }
  .sdk-embed-page.dark .verification-sdk .consent-checkbox {
    background: var(--gray-50);
    border-color: var(--gray-200);
    color: var(--gray-600);
  }
  .sdk-embed-page.dark .verification-sdk .consent-checkbox:has(input:checked) { border-color: var(--primary); }
  .sdk-embed-page.dark .verification-sdk .document-type-card,
  .sdk-embed-page.dark .verification-sdk .form-group input,
  .sdk-embed-page.dark .verification-sdk .form-group select,
  .sdk-embed-page.dark .verification-sdk .date-select,
  .sdk-embed-page.dark .verification-sdk .address-autocomplete input,
  .sdk-embed-page.dark .verification-sdk .autocomplete-dropdown,
  .sdk-embed-page.dark .verification-sdk .preview-badge,
  .sdk-embed-page.dark .verification-sdk .processing-step .check-icon,
  .sdk-embed-page.dark .verification-sdk .questionnaire-step textarea,
  .sdk-embed-page.dark .verification-sdk .capture-option:hover {
    background: #17151f;
    color: #f3f4f6;
    border-color: #2e2a3b;
  }
  .sdk-embed-page.dark .verification-sdk .submit-info { background: #1f1a2e; color: #f3f4f6; }

  @media (max-width: 480px) {
    .sdk-embed-page .verification-sdk { max-width: 100%; }
  }
`;
